import { Dispatch, SetStateAction, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IEditionSecondaryCodes } from "../../hooks/useSecondaryCodeRequirementsColumnsInterface";
import { IEventReportRow } from "../../SecondaryCodeRequerimentsInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { DropdownSecondaryCodeRequirements, IOptionSecondaryCodes } from "../Dropdown";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";

export const FailureTypeDetailsDropdown = ({
    edit,
    setEdit,
    row,
}: {
    edit: IEditionSecondaryCodes[];
    setEdit: Dispatch<SetStateAction<IEditionSecondaryCodes[]>>;
    row: IEventReportRow;
}) => {
    const { i18n } = useTranslation();
    const { user_id } = useContext(UserContext);
    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);
    const selectedFailureTypeId = edit[existingEdition]?.changes?.failure_type_id;
    const { loading, failureTypes } = EventReportsEntity.GetFailureTypesOptionsForEdition(
        row.machine_center.machine_center_id,
    );
    const failureTypesOptions: IOptionSecondaryCodes[] = useMemo(
        () =>
            failureTypes.map((failureType) => ({
                id: failureType.failure_type_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureType as unknown as IFailureType,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                ),
            })),
        [i18n, failureTypes],
    );

    const handleFailureTypeUpdate = async (id: number) => {
        if (existingEdition !== -1) {
            const failureTypeSelected = failureTypes.find((ft) => ft.failure_type_id === id);
            let editExisting = [...edit];
            editExisting[existingEdition] = {
                ...editExisting[existingEdition],
                location_specific: !!failureTypeSelected?.is_location_specific,
                changes: {
                    ...editExisting[existingEdition]?.changes,
                    failure_type_id: id,
                    area_id: undefined,
                    functional_location_id: undefined,
                    failure_code_id: undefined,
                    secondary_failure_code_id: undefined,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            setEdit(editExisting);
        }
    };

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : (
        <DropdownSecondaryCodeRequirements
            obligatory
            inlineStyle={{ width: "80px" }}
            optionSelected={selectedFailureTypeId}
            options={failureTypesOptions}
            handleUpdate={handleFailureTypeUpdate}
        />
    );
};
